import Section from '@components/atoms/Section';
import { Heading2, Heading3 } from '@components/atoms/Typography';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import GridLinkList from '@components/molecules/list/GridLinkList';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import {
  SliceButtonLink,
  WrapperListGrid
} from '@components/molecules/SliceZone';
import DefaultTable from '@components/molecules/table/DefaultTable';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import FAQBlock from '@components/organisms/seo/FAQBlock';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import ibgeAPI from '@lib/data/ibgeAPI';
import airtableBases, { StateStats } from '@lib/helpers/airtableBases';
import { getInsurancePageInfo, InsurancePageId } from '@lib/helpers/texts';
import prisma from '@lib/services/prisma';
import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

interface Props {
  id: InsurancePageId;
  stats: StateStats;
  districts: string[];
  brands: { brand: string; brandSlug: string }[];
}

const GridList = styled.ul`
  margin: 32px 0;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  &&& {
    list-style: none;
    padding: 0;

    li {
      margin: 0;
    }
  }
`;

const Page = ({ id, stats, districts, brands }: Props) => {
  const info = getInsurancePageInfo(id);

  if (!info) return <PageContainer loading />;

  return (
    <PageContainer
      customAmpPage
      title={`${info?.productName} ${
        stats.nome
      } | Cotação seguro ${stats.uf.toUpperCase()}`}
      metaDescription={`Procurando seguro para ${info?.name} no estado de ${
        stats.nome
      }? Com a Luna você consegue seguro para ${
        info.name
      } de forma totalmente digital e barato em ${stats.uf.toUpperCase()}.`}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
    >
      <DefaultHeader
        title={`${info?.productName} em ${stats.nome}`}
        subtitle={`Faça uma cotacão de seguro para ${info?.name}s no estado de ${stats.nome}`}
        buttonText="Ver Preço do Seguro"
        pastel
      />
      <RichTextSection>
        <p>
          Você mora no estado da <strong>{stats.nome}</strong> e está pensando
          em comprar um <strong>{info.name}</strong>, ou mesmo já o tem, e quer
          garantir uma maior segurança para seu patrimônio? Então você precisa
          contratar um {info.productName} no estado de {stats.nome}.
        </p>
        <p>
          Com uma população de {stats.populacao.toLocaleString()} segundo o{' '}
          <a
            href={`https://www.ibge.gov.br/cidades-e-estados/${stats.uf}`}
            rel="noreferrer"
            target="_blank"
          >
            IBGE
          </a>{' '}
          e{' '}
          <strong>
            mais de {stats.veiculos.toLocaleString()} veículos rodando nas
            estradas do estado
          </strong>
          , o estado de {stats.nome} conta com uma área territorial de{' '}
          {stats.area.toLocaleString()}
          km².
        </p>
        <Heading2>Vantagens de comparar um {info.productName} online</Heading2>
        <ul>
          <li>Economizar Dinheiro</li>
          <li>Poupar Tempo</li>
          <li>Escolher a Melhor Seguradora</li>
        </ul>
        <SliceButtonLink
          slice={{
            primary: {
              text: `Simular Seguro`,
              href: ROUTES.startForm(),
            },
          }}
        />

        <Heading2>Compare o seguro auto para marcas de {info.name}</Heading2>
        <WrapperListGrid>
          <GridLinkList
            deskTemplate="1fr 1fr 1fr 1fr"
            links={brands.map((brand) => ({
              path: ROUTES.insurancePage.brand(id, brand.brandSlug),
              label: `Preço do seguro ${brand.brand}`,
            }))}
          />
        </WrapperListGrid>
        <FAQBlock
          title={`Perguntas frequentes sobre seguro em  ${stats.nome}`}
          items={[
            {
              question: `Seguro de ${info.name} barato em ${stats.nome}?`,
              answer: (
                <>
                  <p>
                    Você pode{' '}
                    <Link passHref href={ROUTES.startForm()}>
                      <a>
                        contratar um seguro de {info?.name} barato em{' '}
                        {stats.nome}
                      </a>
                    </Link>{' '}
                    com a Luna em menos de 5 minutos
                  </p>
                </>
              ),
            },
            {
              question: `Melhor seguro de ${info.name} em ${stats.nome}?`,
              answer: (
                <>
                  <p>
                    O melhor seguro depende das suas necessidades, com a Luna
                    você consegue comparar as{' '}
                    <Link passHref href={ROUTES.startForm()}>
                      <a>
                        melhores seguradoras de
                        {` ${info?.name}`} em
                        {` ${stats.uf.toUpperCase()}`}
                      </a>
                    </Link>{' '}
                    totalmente online
                  </p>
                </>
              ),
            },
            {
              question: `Corretora de seguro em ${stats.nome}?`,
              answer: (
                <>
                  <p>
                    A Luna é uma corretora de seguros para {info.name} que
                    atende todo o estado de {stats.nome}
                  </p>
                </>
              ),
            },
          ]}
        />

        <Heading2>
          Sobre {stats.nome} - {stats.uf.toUpperCase()}
        </Heading2>
        <p>
          {stats.nome} tem um total de {districts.length.toLocaleString()}{' '}
          distritos, sua capital é {stats.capital} e quem nasce em {stats.nome}{' '}
          é chamado de {stats.gentilico}/ Seu governador atual é{' '}
          {stats.governador.toLocaleUpperCase()}
        </p>
        <DefaultTable
          heads={[{ text: 'Informação' }, { text: 'Valores' }]}
          rows={[
            [
              { text: 'Área Territorial' },
              { text: `${stats.area.toLocaleString()} km²` },
            ],
            [
              { text: 'População estimada' },
              { text: `${stats.populacao.toLocaleString()} pessoas` },
            ],
            [
              { text: 'Densidade demográfica' },
              { text: `${stats.densidade.toLocaleString()} hab/km²` },
            ],
            [
              { text: 'Matrículas no ensino fundamental' },
              { text: `${stats.matriculas.toLocaleString()} matrículas` },
            ],
            [
              { text: 'IDH - Índice de desenvolvimento humano' },
              { text: stats.idh.toLocaleString() },
            ],
            [
              { text: 'Receitas realizadas' },
              { text: stats.receitas.toLocaleString() },
            ],
            [
              { text: 'Despesas empenhadas' },
              { text: stats.despesas.toLocaleString() },
            ],
            [
              { text: 'Rendimento mensal domiciliar per capita' },
              { text: stats.rendimento.toLocaleString() },
            ],
            [
              { text: 'Total de veículos' },
              { text: `${stats.veiculos.toLocaleString()} veículos` },
            ],
          ]}
        />
        {!!districts.length && (
          <>
            <Heading3>
              Confira todos os distritos do Estado {stats.nome}
            </Heading3>
            <GridList>
              {districts.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </GridList>
          </>
        )}
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: ROUTES.root,
            },
            {
              label: info?.name,
              href: ROUTES.insurancePage.home(id),
            },
            {
              label: 'Estados',
              href: ROUTES.insurancePage.states(id),
            },
            {
              label: stats.nome,
              href: ROUTES.insurancePage.state(id, stats.uf),
            },
          ]}
        />
      </Section>
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: ['/seguro-carro/estados/sp', '/seguro-moto/estados/rj'],
  fallback: true,
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { insurancePage, uf } = params as {
    insurancePage: InsurancePageId;
    uf: string;
  };

  const info = getInsurancePageInfo(insurancePage);

  if (!info) return { notFound: true };

  const [stats, districts, brands] = await Promise.all([
    airtableBases.getStatsByUf(uf),
    ibgeAPI.districts(uf),
    prisma.fipeModel.findMany({
      where: {
        vehicleType: info.type,
      },
      distinct: ['brand'],
      orderBy: { brand: 'asc' },
      select: {
        brand: true,
        brandSlug: true,
      },
    }),
  ]);

  if (!stats) return { notFound: true };

  return {
    props: { id: insurancePage, stats, districts, brands },
    revalidate: 3600,
  };
};

export default Page;
